import { keyBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'modules/domains/list';
import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains/foldering';
import noop from 'utils/noop';
import { useModalContext } from 'utils/contexts/modal';
import { usePaginationContext } from 'utils/contexts/pagination';
import { shouldMoveToPreviousPage } from 'utils/shouldMoveToPreviousPage';
import { isSelectionContent, isSelectionFolder } from 'utils/contexts/selection';
import { useMoveListsAndFoldersMutation } from '../../../hooks/folders';
import { useListsContext, useSelectionContext } from '../../../contexts';

export const useMoveToListFoldersModalState = () => {
  const { t } = useTranslation();
  const [destinationName, setDestinationName] = useState<string>('');
  const { rootFolderId, refetch, paginationInfo, folderId } = useListsContext();
  const { active } = useSelectionContext();
  const { setCurrentPage, currentPage } = usePaginationContext();
  const { hideModal } = useModalContext();

  const [moveListsAndFolders] = useMoveListsAndFoldersMutation({
    onComplete: hideModal,
    onError: noop,
    onSuccess: (data) => {
      const numMovedContent = data?.moveListContentsAndFolders?.movedContent?.length;
      if (!numMovedContent) return;
      if (shouldMoveToPreviousPage({ ...paginationInfo, numMovedContent })) {
        setCurrentPage(currentPage - 1);
        return;
      }
      refetch();
    },
    destinationName,
    itemsById: keyBy(active, 'id'),
  });

  const onConfirm = useCallback(
    (
      folderIds: FolderId[],
      contentIds: number[],
      destination: AncestorIdData & AncestorNameData,
    ) => {
      setDestinationName(destination?.name === '__root__' ? 'Main' : destination?.name);
      moveListsAndFolders({
        variables: {
          contentIds,
          destinationId: destination.id === rootFolderId ? null : destination.id,
          folderIds,
        },
      });
    },
    [setDestinationName, moveListsAndFolders, rootFolderId],
  );

  const contentAndFolderData = useMemo(() => {
    const selectedFolders = active.filter(isSelectionFolder);
    const selectedContent = active.filter(isSelectionContent);

    const folderIds = selectedFolders.map(({ id }) => id);
    const contentIds = selectedContent.map(({ id }) => id);

    return {
      folderIds,
      contentIds,
    };
  }, [active]);

  return {
    onClose: hideModal,
    onConfirm,
    contentItemName: t('modals.moveListContentsAndFolders.contentItemName'),
    rootFolderId,
    currentFolderId: folderId,
    ...contentAndFolderData,
  };
};
